import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Cake, Trophy, PartyPopper, Phone, CalendarCheck, RefreshCw, AlertTriangle, ArrowRight, CheckCircle2, ListChecks } from "lucide-react";
import { endpoints } from "../lib/api";
import { useAuth } from "../lib/auth";
import { toast } from "sonner";

/**
 * Today's Focus - the dashboard's hero widget.
 *
 * Surfaces the 5 most important actions for the day. Three sources:
 *   1. Backend tasks (call leads, confirm trials, renewals, at-risk).
 *   2. Frontend-computed celebration tasks (birthdays + anniversaries +
 *      milestones) from member data - zero new backend calls.
 *
 * Each task carries a semantic tone (leads=amber, trials=orange, renewal=growth,
 * at_risk=red, celebration=ai/purple) and a clear primary action (Message /
 * Confirm / Renew / Open).
 */

const TONE = {
  leads:  { ring: "ring-leads/20",  bg: "bg-leads/10",  fg: "text-leads",  bar: "bg-leads"  },
  trials: { ring: "ring-trials/20", bg: "bg-trials/10", fg: "text-trials", bar: "bg-trials" },
  growth: { ring: "ring-growth/20", bg: "bg-growth/10", fg: "text-growth", bar: "bg-growth" },
  risk:   { ring: "ring-risk/20",   bg: "bg-risk/10",   fg: "text-risk",   bar: "bg-risk"   },
  ai:     { ring: "ring-ai/20",     bg: "bg-ai/10",     fg: "text-ai",     bar: "bg-ai"     },
  tasks:  { ring: "ring-tasks/20",  bg: "bg-tasks/10",  fg: "text-tasks",  bar: "bg-tasks"  },
  ops:    { ring: "ring-foreground/10", bg: "bg-muted", fg: "text-muted-foreground", bar: "bg-ops" },
};

function todayKey() {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}

// Helper: parse ISO date string, return {month, day} for birthday matching.
function md(iso) {
  if (!iso) return null;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return null;
  return { m: d.getMonth(), day: d.getDate() };
}

function yearsBetween(iso, now) {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return 0;
  return now.getFullYear() - d.getFullYear() - (
    (now.getMonth() < d.getMonth() || (now.getMonth() === d.getMonth() && now.getDate() < d.getDate())) ? 1 : 0
  );
}

function buildCelebrationTasks(members) {
  if (!Array.isArray(members) || members.length === 0) return [];
  const now = new Date();
  const today = { m: now.getMonth(), day: now.getDate() };
  const out = [];

  for (const m of members) {
    const dob = md(m.date_of_birth);
    if (dob && dob.m === today.m && dob.day === today.day) {
      out.push({
        id: `birthday-${m.id}`,
        type: "birthday",
        title: `Wish ${m.name} happy birthday`,
        subtitle: "Birthday today",
        Icon: Cake,
        tone: "ai",
        member_id: m.id,
        priority: "high",
      });
    }
    const join = md(m.start_date || m.created_at);
    if (join && join.m === today.m && join.day === today.day) {
      const years = Math.max(1, yearsBetween(m.start_date || m.created_at, now));
      out.push({
        id: `anniversary-${m.id}`,
        type: "anniversary",
        title: `Congratulate ${m.name} on ${years} year${years > 1 ? "s" : ""} of membership`,
        subtitle: "Membership anniversary",
        Icon: PartyPopper,
        tone: "ai",
        member_id: m.id,
        priority: "medium",
      });
    }
    const checkins = (m.check_ins || []).length;
    if ([50, 100, 200, 365, 500].includes(checkins)) {
      out.push({
        id: `milestone-${m.id}`,
        type: "milestone",
        title: `Congratulate ${m.name} on ${checkins} check-ins`,
        subtitle: `${checkins} sessions completed`,
        Icon: Trophy,
        tone: "ai",
        member_id: m.id,
        priority: "low",
      });
    }
  }
  return out;
}

function toneForTask(t) {
  if (t.tone) return t.tone;
  switch (t.type) {
    case "follow_up": return "leads";
    case "trial_today":
    case "trial_followup":
    case "trial": return "trials";
    case "renewal":
    case "scheduled_reminder": return t.kind?.startsWith("renewal") ? "growth" : "trials";
    case "inactivity":
    case "at_risk": return "risk";
    case "birthday":
    case "anniversary":
    case "milestone": return "ai";
    default: return "tasks";
  }
}

function iconForTask(t) {
  if (t.Icon) return t.Icon;
  switch (t.type) {
    case "follow_up": return Phone;
    case "trial_today":
    case "trial": return CalendarCheck;
    case "renewal":
    case "scheduled_reminder": return RefreshCw;
    case "inactivity":
    case "at_risk": return AlertTriangle;
    default: return ListChecks;
  }
}

function actionLabel(t) {
  switch (t.type) {
    case "follow_up": return "Call";
    case "trial_today":
    case "trial": return "Confirm";
    case "renewal":
    case "scheduled_reminder": return "Renew";
    case "inactivity":
    case "at_risk": return "Message";
    case "birthday":
    case "anniversary":
    case "milestone": return "Wish";
    default: return "Open";
  }
}

export default function TodayFocusCard({ tasks = [], members = [], loading }) {
  const celebrations = useMemo(() => buildCelebrationTasks(members), [members]);

  // Merge backend tasks (already includes manual + auto) with celebration tasks.
  const merged = useMemo(() => {
    const today = todayKey();
    const open = (tasks || [])
      .filter(t => t.status !== "done")
      .map(t => ({
        id: t.id,
        type: t.auto_kind || t.type || "manual",
        title: t.title,
        subtitle: t.subtitle || (t.due_date ? `Due ${t.due_date}` : ""),
        priority: t.priority || "medium",
        due_date: t.due_date,
        member_id: t.linked_member_id,
        lead_id: t.linked_lead_id,
        backend_id: t.id,
      }));

    const order = { high: 0, medium: 1, low: 2 };
    const all = [...celebrations, ...open];
    all.sort((a, b) => {
      // celebrations float to the top so they get acted on while feelings matter
      const aCel = ["birthday","anniversary","milestone"].includes(a.type) ? 0 : 1;
      const bCel = ["birthday","anniversary","milestone"].includes(b.type) ? 0 : 1;
      if (aCel !== bCel) return aCel - bCel;
      return (order[a.priority] ?? 1) - (order[b.priority] ?? 1);
    });
    return all.slice(0, 6);
  }, [tasks, celebrations]);

  const totalOpen = merged.length + (tasks || []).filter(t => t.status !== "done" && !merged.find(m => m.backend_id === t.id)).length;

  const [doneIds, setDoneIds] = useState(new Set());
  const markDone = async (t) => {
    if (!t.backend_id) {
      setDoneIds(s => new Set([...s, t.id]));
      toast.success("Nice. Marked done.");
      return;
    }
    setDoneIds(s => new Set([...s, t.id]));
    try { await endpoints.updateTask(t.backend_id, { status: "done" }); }
    catch { toast.error("Could not update"); }
  };

  return (
    <section
      data-testid="today-focus-card"
      className="rounded-2xl border border-border bg-card p-5 md:p-6"
    >
      <div className="flex items-center justify-between mb-5 gap-3 flex-wrap">
        <div className="flex items-center gap-2.5">
          <span className="inline-flex items-center justify-center w-9 h-9 rounded-xl bg-tasks/10 text-tasks">
            <ListChecks className="w-4.5 h-4.5" strokeWidth={2.4} />
          </span>
          <div>
            <p className="text-[11px] font-semibold uppercase tracking-[0.16em] text-muted-foreground/70">Today's Focus</p>
            <h3 className="font-display text-lg font-bold text-foreground tracking-tight leading-tight">
              {loading ? "Pulling your day together..." : merged.length === 0 ? "You're all caught up." : `${merged.length} action${merged.length === 1 ? "" : "s"} for today`}
            </h3>
          </div>
        </div>
        <Link
          to="/tasks"
          data-testid="today-focus-view-all"
          className="inline-flex items-center gap-1.5 text-xs font-semibold text-tasks hover:text-tasks/80 transition-colors"
        >
          View all <ArrowRight className="w-3.5 h-3.5" />
        </Link>
      </div>

      {loading ? (
        <div className="space-y-2">
          {[0, 1, 2].map(i => (
            <div key={i} className="h-14 rounded-xl bg-muted/40 animate-pulse" />
          ))}
        </div>
      ) : merged.length === 0 ? (
        <div className="text-center py-10" data-testid="today-focus-empty">
          <div className="inline-flex items-center justify-center w-12 h-12 rounded-2xl bg-growth/10 text-growth mb-3">
            <CheckCircle2 className="w-6 h-6" strokeWidth={2.2} />
          </div>
          <p className="font-display text-base font-semibold text-foreground">You're all caught up today.</p>
          <p className="text-sm text-muted-foreground mt-1">Tomorrow's tasks will appear here as your day starts.</p>
        </div>
      ) : (
        <ul className="space-y-2" data-testid="today-focus-list">
          {merged.map(t => {
            const tone = TONE[toneForTask(t)];
            const Icon = iconForTask(t);
            const done = doneIds.has(t.id);
            const linkHref = t.member_id
              ? `/members/${t.member_id}?wa=task`
              : t.lead_id
                ? `/leads/${t.lead_id}?wa=task`
                : "/tasks";
            return (
              <li
                key={t.id}
                data-testid={`focus-task-${t.id}`}
                className={`group flex items-center gap-3 px-3.5 py-3 rounded-xl border border-border bg-card hover:bg-foreground/[0.02] transition-colors ${done ? "opacity-50" : ""}`}
              >
                <span className={`w-9 h-9 rounded-lg flex items-center justify-center shrink-0 ${tone.bg} ${tone.fg}`}>
                  <Icon className="w-4 h-4" strokeWidth={2.4} />
                </span>
                <div className="flex-1 min-w-0">
                  <p className={`text-sm font-medium text-foreground truncate ${done ? "line-through" : ""}`}>{t.title}</p>
                  {t.subtitle && (
                    <p className="text-xs text-muted-foreground mt-0.5 truncate">{t.subtitle}</p>
                  )}
                </div>
                <Link
                  to={linkHref}
                  data-testid={`focus-task-action-${t.id}`}
                  className={`text-xs font-semibold px-3 py-1.5 rounded-lg shrink-0 ${tone.bg} ${tone.fg} hover:opacity-80 transition-opacity`}
                >
                  {actionLabel(t)}
                </Link>
                <button
                  type="button"
                  onClick={() => markDone(t)}
                  aria-label="Mark done"
                  data-testid={`focus-task-done-${t.id}`}
                  className="w-7 h-7 rounded-md text-muted-foreground/60 hover:text-growth hover:bg-growth/10 transition-colors flex items-center justify-center"
                >
                  <CheckCircle2 className="w-4 h-4" strokeWidth={2.2} />
                </button>
              </li>
            );
          })}
        </ul>
      )}

      {!loading && totalOpen > merged.length && (
        <Link
          to="/tasks"
          data-testid="today-focus-bottom-link"
          className="mt-4 inline-flex w-full items-center justify-center gap-1.5 text-xs font-semibold text-tasks hover:text-tasks/80"
        >
          {totalOpen - merged.length} more in Tasks <ArrowRight className="w-3.5 h-3.5" />
        </Link>
      )}
    </section>
  );
}
